/**
 * Which tender wizard steps a role may open.
 *
 * ⚠️ WHY PER-STEP KEYS EXIST.
 *
 * Permissions were coarse — tender.view, tender.edit — and the wizard rendered whatever step it
 * was navigated to. A broker who should only compare offers and generate the proposal had no way
 * to be given exactly that: granting tender.edit handed them client details, retailer emails and
 * offer entry as well.
 *
 * One key per step, so an admin composes the access a role actually needs by ticking two boxes.
 *
 * ⚠️ A MISSING ROW MEANS DENIED. PermissionsContext.can() is `permissions.has(key)`, and the
 * server returns only rows with granted = true — so adding these keys denies every step to every
 * existing role until its rows exist. Migration 114 backfills them as granted for every role
 * already configured, which is why shipping this does not lock current members out of the wizard.
 * New roles are seeded granted = false by createOrgRole, so a restricted role starts with
 * nothing and the admin opts it in.
 *
 * ⚠️ UI GATING ONLY. These hide steps and block navigation to them; they do not stop a crafted
 * API call. That was a deliberate scope decision — the figures a step writes are still guarded by
 * the coarse tender.* permissions.
 */

export type TenderStep = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

/** Step number → permission key. The numbers match DocumentsNew's switch and TenderDetails' picker. */
export const TENDER_STEP_PERMISSIONS: Record<TenderStep, string> = {
  1: 'tender.step.client_info',
  2: 'tender.step.retailer_comms',
  3: 'tender.step.email_processing',
  4: 'tender.step.offers',
  5: 'tender.step.compare_select',
  6: 'tender.step.recommended',
  7: 'tender.step.template',
  8: 'tender.step.offer_details',
  9: 'tender.step.generate',
};

export const TENDER_STEP_NUMBERS = Object.keys(TENDER_STEP_PERMISSIONS)
  .map(Number)
  .sort((a, b) => a - b) as TenderStep[];

/** Every step key, for the server's ALL_PERMISSION_KEYS and the admin tab. */
export const TENDER_STEP_PERMISSION_KEYS = TENDER_STEP_NUMBERS.map(n => TENDER_STEP_PERMISSIONS[n]);

/** Is this a step the given role may open? Admins always may. */
export const canAccessStep = (
  can: (permission: string) => boolean,
  step: number,
): boolean => {
  const key = TENDER_STEP_PERMISSIONS[step as TenderStep];
  if (!key) return true;          // not a wizard step — nothing to gate
  return can(key);
};

/** The steps a role may open, in order. */
export const allowedSteps = (can: (permission: string) => boolean): TenderStep[] =>
  TENDER_STEP_NUMBERS.filter(n => canAccessStep(can, n));

/**
 * Where to send someone who lands on a step they cannot open.
 *
 * The FIRST step they are allowed, so a role granted only Compare & Select and Generate opens on
 * Compare & Select rather than on an error. Null when they may open none — the caller then shows
 * a no-access state rather than redirecting in a loop.
 */
export const firstAllowedStep = (can: (permission: string) => boolean): TenderStep | null =>
  allowedSteps(can)[0] ?? null;

/**
 * The next step a role may open after this one, for the wizard's Next button.
 *
 * Skipping forward over denied steps is what makes a two-step role usable: Compare & Select's
 * Next lands on Generate instead of on a step the user cannot see.
 */
export const nextAllowedStep = (
  can: (permission: string) => boolean,
  current: number,
): TenderStep | null => allowedSteps(can).find(n => n > current) ?? null;

/** The previous step a role may open, for the Back button. */
export const previousAllowedStep = (
  can: (permission: string) => boolean,
  current: number,
): TenderStep | null => {
  const before = allowedSteps(can).filter(n => n < current);
  return before.length > 0 ? before[before.length - 1] : null;
};
